import { Show } from "../types/Show";
import { Mix } from "../types/Mix";
import { Presenter, PresenterResponse } from "../types/Presenter";
import { RoutineScheduleResponse } from "../types/Schedule";
import apiRetryService, { DEFAULT_RETRY_CONFIG } from './apiRetryService';
import { debugError, debugLog, debugGroup } from '../utils/debugLog';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const API_KEY = process.env.REACT_APP_API_KEY;
const LIVE_SHOW_URL = "https://api.eruptionradio.uk/schedule/now";
const RECORDING_CDN_BASE =
  process.env.REACT_APP_RECORDING_CDN_BASE ||
  "https://station-recordings.b-cdn.net/";

const normalizeRecordingCdnUrl = (url?: string | null): string | undefined => {
  if (!url) return undefined;
  return url.replace(
    /^http:\/\/station-recordings\.b-cdn\.net\//,
    "https://station-recordings.b-cdn.net/"
  );
};

// Construct full URLs
const PRESENTER_API_URL = `${API_BASE_URL}/presenter?station_id=eruption`;
const EXCLUDED_PRESENTER_IDS = (process.env.REACT_APP_EXCLUDED_PRESENTER_IDS || "")
  .split(",")
  .map((id) => id.trim())
  .filter(Boolean)
  .map((id) => parseInt(id, 10))
  .filter((id) => !Number.isNaN(id));

interface ApiRequestError extends Error {
  originalError: unknown;
  url: string;
}

export interface ApiResponse {
  success: boolean;
  data: Show[];
  error?: string;
}

export interface LiveShowResponse {
  success: boolean;
  data: Show | null;
  error?: string;
}

export interface MixesResponse {
  success: boolean;
  data?: {
    mixes: Mix[];
    pagination?: {
      limit?: number;
      hasMore?: boolean;
      lastKey?: string | null;
      totalReturned?: number;
    };
  };
  error?: string;
}

export class ApiService {
  private static parseMaxAgeDays(rawValue: string | undefined): number {
    if (!rawValue) {
      return 30;
    }

    const trimmed = rawValue.trim();
    if (!trimmed) {
      return 30;
    }

    const parsed = Number.parseInt(trimmed, 10);
    if (!Number.isFinite(parsed)) {
      return 30;
    }

    return parsed;
  }

  private static getRecordingsMaxAgeDays(): number {
    const parsed = this.parseMaxAgeDays(process.env.REACT_APP_RECORDINGS_MAX_AGE_DAYS);

    if (parsed === -1) {
      return -1;
    }

    if (parsed <= 0) {
      return 30;
    }

    return parsed;
  }

  private static getEarliestAllowedRecordingYmd(): number | null {
    const maxAgeDays = this.getRecordingsMaxAgeDays();
    if (maxAgeDays === -1) {
      return null;
    }

    const earliest = new Date();
    earliest.setHours(0, 0, 0, 0);
    earliest.setDate(earliest.getDate() - maxAgeDays);
    return earliest.getFullYear() * 10000 + (earliest.getMonth() + 1) * 100 + earliest.getDate();
  }

  private static parseYmdNumber(dateString: string | undefined): number | null {
    if (!dateString) {
      return null;
    }

    const parts = dateString.split("-");
    if (parts.length !== 3) {
      return null;
    }

    const year = Number.parseInt(parts[0] ?? "", 10);
    const month = Number.parseInt(parts[1] ?? "", 10);
    const day = Number.parseInt(parts[2] ?? "", 10);

    if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
      return null;
    }

    return year * 10000 + month * 100 + day;
  }

  private static isWithinRecordingsRetentionWindow(dateStartTime: string | undefined): boolean {
    const earliestYmd = this.getEarliestAllowedRecordingYmd();
    if (earliestYmd === null) {
      return true;
    }

    if (!dateStartTime) {
      return false;
    }

    const [datePart] = dateStartTime.split("#");
    const showYmd = this.parseYmdNumber(datePart);
    if (showYmd === null) {
      return false;
    }

    return showYmd >= earliestYmd;
  }

  private static applyRecordingsRetention<T extends { date_start_time: string }>(shows: T[]): T[] {
    const earliestYmd = this.getEarliestAllowedRecordingYmd();
    if (earliestYmd === null) {
      return shows;
    }

    return shows.filter((show) => this.isWithinRecordingsRetentionWindow(show.date_start_time));
  }

  private static async makeRequest(
    url: string,
    requiresApiKey: boolean = true
  ): Promise<any> {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    if (requiresApiKey) {
      if (!API_KEY) {
        throw new Error("API_KEY is not configured in environment variables");
      }
      headers["x-api-key"] = API_KEY;
    }

    try {
      const response = await apiRetryService.fetchWithRetry(url, {
        method: "GET",
        headers,
      }, DEFAULT_RETRY_CONFIG);

      const data = await response.json();
      return data;
    } catch (error) {
      debugError(`API request failed for ${url}:`, error);
      
      // Enhance error with context
      const enhancedError: ApiRequestError = Object.assign(
        new Error(
          `API request failed: ${error instanceof Error ? error.message : 'Unknown error'}`
        ),
        {
          originalError: error,
          url,
        }
      );
      
      throw enhancedError;
    }
  }

  static async getShows(): Promise<ApiResponse> {
    try {
      if (!API_BASE_URL) {
        throw new Error(
          "API_BASE_URL is not configured in environment variables"
        );
      }

      // ✅ FIX: Use the same URL building pattern as getShowsPaginated
      // Build query parameters like the working paginated method
      const params = new URLSearchParams();
      params.set('station_id', 'eruption');
      
      const url = `${API_BASE_URL}/recording?${params.toString()}`;
      const data = await this.makeRequest(url, true);

      // Transform the API response to match our Show interface
      // Handle both paginated response format and direct array format
      let shows: Show[] = [];
      if (data && data.shows && Array.isArray(data.shows)) {
        // Paginated response format
        shows = data.shows.map(this.transformApiShow);
      } else if (Array.isArray(data)) {
        // Direct array format
        shows = data.map(this.transformApiShow);
      }

      shows = this.applyRecordingsRetention(shows);

      return {
        success: true,
        data: shows,
      };
    } catch (error) {
      debugError("Error fetching shows:", error);
      return {
        success: false,
        data: [],
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  }

  static async getPresenters(): Promise<PresenterResponse> {
    try {
      if (!API_BASE_URL) {
        throw new Error(
          "API_BASE_URL is not configured in environment variables"
        );
      }

      const data = await this.makeRequest(PRESENTER_API_URL, true);

      // Transform the API response to match our Presenter interface
      // Filter out inactive presenters and transform the rest
      const presenters: Presenter[] = Array.isArray(data)
        ? data
            // Only include active presenters
            .filter((presenter) => presenter.active === true)
            // Exclude presenters configured in env (by numeric or string id)
            .filter((presenter) => {
              const presenterNumericId = presenter.numeric_id ?? presenter.presenter_id;
              if (typeof presenterNumericId === "number") {
                return !EXCLUDED_PRESENTER_IDS.includes(presenterNumericId);
              }
              return true;
            })
            .map(this.transformApiPresenter)
        : [];

      return {
        success: true,
        data: presenters,
      };
    } catch (error) {
      debugError("Error fetching presenters:", error);
      return {
        success: false,
        data: [],
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  }

  static async getRoutineSchedule(options?: {
    stationId?: string;
    presenterId?: string;
  }): Promise<RoutineScheduleResponse> {
    try {
      if (!API_BASE_URL) {
        throw new Error(
          "API_BASE_URL is not configured in environment variables"
        );
      }

      // Build query parameters
      const params = new URLSearchParams();
      params.set('station_id', options?.stationId || 'eruption');
      
      if (options?.presenterId) {
        params.set('presenter_id', options.presenterId);
      }

      const url = `${API_BASE_URL}/routine-schedule?${params.toString()}`;
      const data = await this.makeRequest(url, true);

      // The API returns the data in the expected format already
      if (data && data.success && Array.isArray(data.data)) {
        return {
          success: true,
          data: data.data,
          count: data.count,
          cached_until: data.cached_until,
        };
      }

      return {
        success: false,
        data: [],
        error: "Invalid response format from routine schedule API",
      };
    } catch (error) {
      debugError("Error fetching routine schedule:", error);
      return {
        success: false,
        data: [],
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  }

  static async getLiveShow(): Promise<LiveShowResponse> {
    try {
      const data = await this.makeRequest(LIVE_SHOW_URL, false); // No API key needed for this endpoint

      debugGroup("Live show API data", () => debugLog(data));

      // Handle the correct API response structure: {"show": {...}, "override_show_title": ""}
      // Check if we have a valid show object
      if (data && data.show && typeof data.show === 'object') {
        // Check if it's not the fallback "Eruption Archives"
        const showName = data.show.name || data.show.show_title || data.show.title;
        if (showName && showName !== "Eruption Archives") {
          const liveShow = this.transformLiveShow(data.show);
          debugLog("✅ Live show detected:", showName);
          return {
            success: true,
            data: liveShow,
          };
        }
      }
      
      // Also check for override_show_title as a fallback
      if (data && data.override_show_title && data.override_show_title.trim() !== "") {
        const liveShow = this.transformLiveShow({
          name: data.override_show_title,
          show_title: data.override_show_title,
          title: data.override_show_title
        });
        debugLog("✅ Live show detected (override):", data.override_show_title);
        return {
          success: true,
          data: liveShow,
        };
      }

      // No live show currently
      debugLog("ℹ️ No live show detected");
      return {
        success: true,
        data: null,
      };
    } catch (error) {
      debugError("❌ Error fetching live show:", error);
      return {
        success: false,
        data: null,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  }

  // Helper method to get audio duration from metadata
  static async getAudioDuration(audioUrl: string): Promise<number> {
    return new Promise((resolve) => {
      const audio = new Audio();
      
      const handleLoadedMetadata = () => {
        cleanup();
        resolve(audio.duration);
      };
      
      const handleError = () => {
        cleanup();
        resolve(0); // Return 0 if we can't get duration
      };
      
      const cleanup = () => {
        audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
        audio.removeEventListener("error", handleError);
      };
      
      audio.addEventListener("loadedmetadata", handleLoadedMetadata);
      audio.addEventListener("error", handleError);
      audio.src = audioUrl;
    });
  }

  // Helper method to format duration in HH:MM format
  static formatDuration(seconds: number): string {
    if (!seconds || seconds === 0) return "Unknown";

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  }

  // Helper function to generate show_id from show name and date/time
  private static generateShowId(showName: string, dateStartTime: string): string {
    // Extract date and time from date_start_time format: "YYYY-MM-DD#HH:MM:SS"
    const [datePart, timePart] = dateStartTime.split('#');
    const timeHour = timePart ? timePart.split(':')[0] + '00' : '1400'; // Default to 1400 if no time
    
    const cleanShowName = showName.toLowerCase()
      .replace(/[^a-z0-9\s&]/g, '')
      .replace(/\s+/g, '-')
      .replace(/&/g, 'and');
    
    return `${cleanShowName}-${datePart}-${timeHour}`;
  }

  // Helper function to parse show_id back to database format
  private static parseShowId(showId: string): { station_id: string; date_start_time: string } | null {
    // Show ID format: "presenter-name-YYYY-MM-DD-HHMM"
    // Database format: station_id="eruption", date_start_time="YYYY-MM-DD#HH:MM:SS"
    
    // Extract the date-time part (last 13 characters: YYYY-MM-DD-HHMM)
    const dateTimeMatch = showId.match(/(\d{4}-\d{2}-\d{2})-(\d{4})$/);
    if (!dateTimeMatch) {
      debugError(`Invalid show ID format: ${showId}. Expected format: presenter-name-YYYY-MM-DD-HHMM`);
      return null;
    }
    
    const [, datePart, timePart] = dateTimeMatch;
    
    // Convert HHMM to HH:MM:SS format
    const hours = timePart.substring(0, 2);
    const minutes = timePart.substring(2, 4);
    const timeFormatted = `${hours}:${minutes}:00`;
    
    return {
      station_id: 'eruption',
      date_start_time: `${datePart}#${timeFormatted}`
    };
  }

  // New method for individual show lookup
  static async getIndividualShow(showId: string): Promise<{
    success: boolean;
    data?: Show;
    error?: string;
  }> {
    try {
      if (!API_BASE_URL) {
        throw new Error(
          "API_BASE_URL is not configured in environment variables"
        );
      }

      // Parse show ID to get database key components
      const parsedKey = this.parseShowId(showId);
      if (!parsedKey) {
        return {
          success: false,
          error: `Invalid show ID format: ${showId}. Expected format: presenter-name-YYYY-MM-DD-HHMM`
        };
      }

      debugLog(`🔍 Individual show lookup: ${showId} -> station_id=${parsedKey.station_id}, date_start_time=${parsedKey.date_start_time}`);

      if (!this.isWithinRecordingsRetentionWindow(parsedKey.date_start_time)) {
        return {
          success: false,
          error: `This recording is no longer available (limited to the last ${this.getRecordingsMaxAgeDays()} days).`,
        };
      }

      // Build query parameters for individual show lookup
      const params = new URLSearchParams();
      params.set('station_id', parsedKey.station_id);
      params.set('date_start_time', parsedKey.date_start_time);

      const url = `${API_BASE_URL}/recording?${params.toString()}`;
      
      try {
        const data = await this.makeRequest(url, true);
        
        // Handle the response
        if (data && data.shows && Array.isArray(data.shows) && data.shows.length > 0) {
          const transformedShow = this.transformApiShow(data.shows[0]);
          debugLog(`✅ Individual show found: ${transformedShow.show} by ${transformedShow.presenter.name}`);
          
          return {
            success: true,
            data: transformedShow,
          };
        } else {
          debugLog(`❌ Individual show not found: ${showId}`);
          return {
            success: false,
            error: `Show not found: ${showId}`
          };
        }
      } catch (error: any) {
        debugError("Error fetching individual show:", error);
        
        // Check if this is a 404 error (show doesn't exist)
        if (error.status === 404 || (error.message && error.message.includes('404'))) {
          debugLog(`❌ Show ${showId} not found (404)`);
          return {
            success: false,
            error: `Show not found: ${showId}`
          };
        }
        
        // For other errors (network, 500, etc.), return generic error
        return {
          success: false,
          error: error instanceof Error ? error.message : "Unknown error occurred",
        };
      }
    } catch (error) {
      debugError("Error fetching individual show:", error);
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  }

  private static transformApiShow(apiShow: any): Show {
    // Build audio URL from filePath if available
    let audioUrl: string | undefined;
    if (apiShow.filePath) {
      // Ensure CDN base URL ends with /
      const cdnBase = RECORDING_CDN_BASE.endsWith("/")
        ? RECORDING_CDN_BASE
        : RECORDING_CDN_BASE + "/";
      // Remove leading / from filePath if present
      const cleanPath = apiShow.filePath.startsWith("/")
        ? apiShow.filePath.substring(1)
        : apiShow.filePath;
      audioUrl = cdnBase + cleanPath;
    }

    const showName = apiShow.show || apiShow.title || "Unknown Show";
    const dateStartTime = apiShow.date_start_time ||
      apiShow.start_time ||
      new Date().toISOString().replace("T", "#").substring(0, 19);

    return {
      show: showName,
      show_id: apiShow.show_id || ApiService.generateShowId(showName, dateStartTime),
      date_start_time: dateStartTime,
      images: {
        "600x600":
          apiShow.images?.["600x600"] || apiShow.image_600 || apiShow.image,
        "300x300":
          apiShow.images?.["300x300"] || apiShow.image_300 || apiShow.image,
        "150x150":
          apiShow.images?.["150x150"] || apiShow.image_150 || apiShow.image,
      },
      mixcloud_url: apiShow.mixcloud_url || apiShow.url || "#",
      presenter: {
        name:
          apiShow.presenter?.name ||
          apiShow.presenter_name ||
          apiShow.dj ||
          "Unknown Presenter",
        genres: apiShow.presenter?.genres || apiShow.genres || "Various",
      },
      isLive: apiShow.isLive || apiShow.is_live || apiShow.live || false,
      // Audio recording properties
      filePath: apiShow.filePath,
      audioUrl: audioUrl,
      duration: apiShow.duration || undefined, // Duration in seconds from API
      selected_length_seconds: apiShow.selected_length_seconds || undefined, // Selected duration from recording
      // Sub-title for show episodes
      sub_title: apiShow.sub_title || undefined,
    };
  }

  private static buildRecordingCdnUrl(filePath?: string | null): string | undefined {
    if (!filePath) return undefined;
    const cdnBase = RECORDING_CDN_BASE.endsWith("/")
      ? RECORDING_CDN_BASE
      : RECORDING_CDN_BASE + "/";
    const cleanPath = filePath.startsWith("/") ? filePath.substring(1) : filePath;
    return normalizeRecordingCdnUrl(cdnBase + cleanPath);
  }

  private static transformApiMix(apiMix: any): Mix {
    return {
      station_id: apiMix.station_id || "eruption",
      mix_id: apiMix.mix_id,
      slug: apiMix.slug || "",
      title: apiMix.title || "Untitled Mix",
      description: apiMix.description || "",
      artist_name: apiMix.artist_name || "Eruption Radio",
      genres: apiMix.genres || "",
      published_at: apiMix.published_at || apiMix.created_at || "",
      recorded_at: apiMix.recorded_at || "",
      recorded_event: apiMix.recorded_event || "",
      recorded_location: apiMix.recorded_location || "",
      type: apiMix.type || "studio",
      featured: apiMix.featured === true,
      audio_file_path: apiMix.audio_file_path || "",
      image_file_path: apiMix.image_file_path || "",
      is_published: apiMix.is_published !== false,
      seo_title: apiMix.seo_title || "",
      seo_description: apiMix.seo_description || "",
      created_at: apiMix.created_at || "",
      updated_at: apiMix.updated_at || "",
      audioUrl: normalizeRecordingCdnUrl(apiMix.audioUrl) || this.buildRecordingCdnUrl(apiMix.audio_file_path),
      imageUrl: normalizeRecordingCdnUrl(apiMix.imageUrl) || this.buildRecordingCdnUrl(apiMix.image_file_path),
    };
  }

  private static transformLiveShow(apiShow: any): Show {
    // Transform the live show API response to match our Show interface
    const currentDate = new Date();
    const dateStr = currentDate.toISOString().split("T")[0]; // YYYY-MM-DD format
    const timeStr =
      apiShow.start_time || currentDate.toTimeString().split(" ")[0]; // HH:mm:ss format
    
    const showName = apiShow.show || apiShow.show_title || apiShow.name || "Live Show";
    const dateStartTime = `${dateStr}#${timeStr}`;

    return {
      show: showName,
      show_id: apiShow.show_id || ApiService.generateShowId(showName, dateStartTime),
      date_start_time: dateStartTime,
      images: apiShow.images || {
        "600x600": undefined,
        "300x300": undefined,
        "150x150": undefined,
      },
      mixcloud_url: apiShow.mixcloud_url || "#",
      presenter: {
        name: apiShow.name || "Unknown Presenter",
        genres: apiShow.genres || "Various",
      },
      isLive: true, // This endpoint only returns live shows
      // Live shows don't have recordings
      filePath: undefined,
      audioUrl: undefined,
      duration: undefined,
      // Sub-title for live show episodes
      sub_title: apiShow.sub_title || undefined,
    };
  }

  private static transformApiPresenter(apiPresenter: any): Presenter {
    // Transform the API response to match our Presenter interface
    debugGroup("Transforming API presenter", () => debugLog(apiPresenter));
    const presenterId = apiPresenter.id ?? apiPresenter.presenter_id;

    // Get the best available image from the images object
    const getPresenterImage = () => {
      if (apiPresenter.images) {
        // Prefer higher resolution images
        return (
          apiPresenter.images["600x600"] ||
          apiPresenter.images["500x500"] ||
          apiPresenter.images["400x400"] ||
          apiPresenter.images["300x300"] ||
          apiPresenter.images["150x150"] ||
          ""
        );
      }
      return "";
    };

    // Use mixcloud_genres as fallback since genres field is currently empty
    const genres =
      apiPresenter.genres || apiPresenter.mixcloud_genres || "Various";

    const derivedSlug =
      (typeof apiPresenter.wordpress_slug === "string" && apiPresenter.wordpress_slug.trim()) ||
      (typeof apiPresenter.slug === "string" && apiPresenter.slug.trim()) ||
      undefined;

    return {
      id:
        (typeof presenterId === "number" ? presenterId : presenterId?.toString()) ||
        apiPresenter.name?.replace(/\s+/g, "-").toLowerCase() ||
        "unknown",
      name: apiPresenter.name || "Unknown Presenter",
      bio: apiPresenter.bio || "",
      image: getPresenterImage(),
      images:
        apiPresenter.images && typeof apiPresenter.images === "object"
          ? apiPresenter.images
          : undefined,
      wordpress_slug: derivedSlug,
      genres: genres,
      social_links: {
        // These would need to be added to the API response in the future
        instagram: undefined,
        twitter: undefined,
        mixcloud: undefined,
        soundcloud: undefined,
      },
      show_count: undefined, // Not available in current API response
      latest_show: undefined, // Not available in current API response
    };
  }

  // New paginated shows API method
  static async getShowsPaginated(options: {
    station_id?: string;
    limit?: number;
    lastKey?: string | null;
    filters?: {
      presenter?: string;
      genre?: string;
      search?: string;
      dateFrom?: string;
      dateTo?: string;
      dayOfWeek?: string;
      hasAudio?: boolean;
    };
  }): Promise<{
    success: boolean;
    data?: any;
    error?: string;
  }> {
    try {
      if (!API_BASE_URL) {
        throw new Error(
          "API_BASE_URL is not configured in environment variables"
        );
      }

      // Build query parameters
      const params = new URLSearchParams();
      params.set('station_id', options.station_id || 'eruption');
      
      if (options.limit) {
        params.set('limit', options.limit.toString());
      }
      
      if (options.lastKey) {
        params.set('lastKey', options.lastKey);
      }

      // Add filter parameters
      if (options.filters) {
        Object.entries(options.filters).forEach(([key, value]) => {
          // Date range filters are applied client-side for now to avoid API pagination errors
          // when combining `lastKey` with dateFrom/dateTo.
          if (key === 'dateFrom' || key === 'dateTo') return;
          if (value !== undefined && value !== null && value !== '') {
            params.set(key, value.toString());
          }
        });
      }

      const url = `${API_BASE_URL}/recording?${params.toString()}`;
      const data = await this.makeRequest(url, true);

      // Transform the shows in the response
      if (data && data.shows && Array.isArray(data.shows)) {
        const originalShows = data.shows.map(this.transformApiShow);
        data.shows = this.applyRecordingsRetention(originalShows);

        const earliestYmd = this.getEarliestAllowedRecordingYmd();
        if (earliestYmd !== null && originalShows.length > 0) {
          const oldestInPage = originalShows[originalShows.length - 1];
          if (!this.isWithinRecordingsRetentionWindow(oldestInPage?.date_start_time)) {
            if (data.pagination) {
              data.pagination.hasMore = false;
              delete data.pagination.lastKey;
            }
          }
        }

        if (earliestYmd !== null && data.shows.length === 0 && data.pagination) {
          data.pagination.hasMore = false;
          delete data.pagination.lastKey;
        }

        // If the user supplied a dateFrom filter, stop paging once we have fetched past it.
        // We don't send dateFrom/dateTo to the API because combining them with lastKey can 500;
        // instead we apply the date range on the client but can still stop early once we cross
        // the lower bound.
        const dateFromBoundaryYmd = this.parseYmdNumber(options.filters?.dateFrom);
        if (dateFromBoundaryYmd !== null && originalShows.length > 0 && data.pagination?.hasMore) {
          const oldestInPage = originalShows[originalShows.length - 1];
          const [oldestDatePart] = (oldestInPage?.date_start_time || '').split('#');
          const oldestDateYmd = this.parseYmdNumber(oldestDatePart);
          if (oldestDateYmd !== null && oldestDateYmd < dateFromBoundaryYmd) {
            data.pagination.hasMore = false;
            delete data.pagination.lastKey;
          }
        }
      }

      return {
        success: true,
        data,
      };
    } catch (error) {
      debugError("Error fetching paginated shows:", error);
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  }

  static async getMixesPaginated(options: {
    station_id?: string;
    limit?: number;
    lastKey?: string | null;
    search?: string;
  } = {}): Promise<MixesResponse> {
    try {
      if (!API_BASE_URL) {
        throw new Error(
          "API_BASE_URL is not configured in environment variables"
        );
      }

      const params = new URLSearchParams();
      params.set("station_id", options.station_id || "eruption");
      if (options.limit) params.set("limit", options.limit.toString());
      if (options.lastKey) params.set("lastKey", options.lastKey);
      if (options.search && options.search.trim().length >= 3) {
        params.set("search", options.search.trim());
      }

      const url = `${API_BASE_URL}/mixes?${params.toString()}`;
      const data = await this.makeRequest(url, true);

      return {
        success: true,
        data: {
          mixes: Array.isArray(data?.mixes)
            ? data.mixes.map((mix: any) => this.transformApiMix(mix))
            : [],
          pagination: data?.pagination,
        },
      };
    } catch (error) {
      console.error("Error fetching mixes:", error);
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  }

  static async getMixBySlug(slugOrId: string): Promise<{
    success: boolean;
    data?: Mix;
    error?: string;
  }> {
    try {
      const target = decodeURIComponent(slugOrId).trim();
      if (!target) throw new Error("Missing mix identifier");

      let lastKey: string | null = null;
      for (let page = 0; page < 5; page++) {
        const response: MixesResponse = await this.getMixesPaginated({
          limit: 100,
          lastKey,
        });

        if (!response.success || !response.data) {
          throw new Error(response.error || "Unable to load mixes");
        }

        const match = response.data.mixes.find(
          (mix) => mix.slug === target || mix.mix_id === target
        );
        if (match) {
          return { success: true, data: match };
        }

        if (!response.data.pagination?.hasMore || !response.data.pagination.lastKey) {
          break;
        }
        lastKey = response.data.pagination.lastKey;
      }

      return { success: false, error: "Mix not found" };
    } catch (error) {
      console.error("Error fetching mix:", error);
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  }
}
